import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Card, Form, Alert } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import { toast } from 'react-toastify';
import { aggiornaQueryResults, resetErrore } from 'features/requests/requestsSlice';

import HtmlHead from 'components/html-head/HtmlHead';
import Loading from 'components/loading/Loading';
import ExcelExport from 'components/export/ExcelExport';

import { richiediDati, richiediQueryDati } from './funzioni';

const { SearchBar } = Search;

// Funzione per generare le colonne dinamicamente dai dati
const getColumns = (data) => {
  if (!data || data.length === 0) {
    return [];
  }

  // Prendo le chiavi dal primo oggetto
  const keys = Object.keys(data[0]);

  return keys.map((key) => ({
    dataField: key,
    text: key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim(),
    sort: true,
  }));
};

const Requests = () => {
  const title = 'Requests';
  const description = 'Manage your requests';

  const { caricamentoInCorso, requests, queryResults, errore } = useSelector((s) => s.requests);
  const dispatch = useDispatch();
  const [selectedRequestId, setSelectedRequestId] = useState('');

  const queryResultsWithId = useMemo(() => {
    return queryResults.map((row, i) => ({ ...row, _id: `row-${i}` }));
  }, [queryResults]);

  // Genero le colonne dinamicamente dai risultati della query
  const columns = getColumns(queryResults);

  useEffect(() => {
    richiediDati();
  }, []);

  // Mostra errori con toast notification
  useEffect(() => {
    if (errore) {
      toast.error(errore, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      // Non resettare l'errore immediatamente, lascia che l'utente lo chiuda dall'Alert
    }
  }, [errore]);

  useEffect(() => {
    if (selectedRequestId) {
      richiediQueryDati(selectedRequestId);
    } else {
      // Reset query results when no request is selected
      dispatch(aggiornaQueryResults([]));
    }
  }, [selectedRequestId, dispatch]);

  if (caricamentoInCorso) {
    return (
      <>
        <HtmlHead title={title} description={description} />
        <Loading />
      </>
    );
  }

  return (
    <>
      <HtmlHead title={title} description={description} />
      <Row>
        <Col>
          <section className="scroll-section" id="title">
            <div className="page-title-container">
              <h1 className="mb-0 pb-0 display-4">{title}</h1>
            </div>
          </section>

          <section className="scroll-section">
            <Card body className="mb-3">
              <Row>
                <Col md={4}>
                  <Form.Label>Select Request</Form.Label>
                  <Form.Select value={selectedRequestId} onChange={(e) => setSelectedRequestId(e.target.value)}>
                    <option value="">-- Select a request --</option>
                    {requests.map((request) => (
                      <option key={request.Id} value={request.Id}>
                        {request.Nome}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
              {errore && (
                <Row className="mt-3">
                  <Col>
                    <Alert variant="danger" onClose={() => dispatch(resetErrore())} dismissible>
                      <Alert.Heading>Errore</Alert.Heading>
                      <p className="mb-0">{errore}</p>
                    </Alert>
                  </Col>
                </Row>
              )}
            </Card>
          </section>

          <section className="scroll-section mb-3">
            <ExcelExport idTabella="tabella-requests" />
          </section>

          <section className="scroll-section">
            <Card body>
              {columns.length > 0 ? (
                <ToolkitProvider keyField="_id" data={queryResultsWithId} columns={columns} search>
                  {(props) => (
                    <>
                      <div className="mb-2">
                        <SearchBar {...props.searchProps} />
                      </div>
                      <BootstrapTable
                        striped
                        id="tabella-requests"
                        bootstrap4
                        condensed
                        wrapperClasses="table-responsive"
                        {...props.baseProps}
                        noDataIndication="Nessun dato disponibile per questa request"
                      />
                    </>
                  )}
                </ToolkitProvider>
              ) : (
                <div className="text-center p-4">
                  <p className="text-muted">{selectedRequestId ? 'Caricamento dati in corso...' : 'Seleziona una request per visualizzare i dati'}</p>
                </div>
              )}
            </Card>
          </section>
        </Col>
      </Row>
    </>
  );
};

export default Requests;
